import React from 'react';
import { apiFetch } from 'api/api';
import { numberWithCommas } from 'utils/Utils';
import { Tooltip } from '@mui/material';
import {
  Dropdown,
  DropdownMenu,
  DropdownItem,
  DropdownToggle,
} from 'reactstrap';
import Loader from 'components/loader/Loader';
import SectionError from 'components/error/SectionError';

/* ── design tokens ── */
const T = {
  bg:   '#F6F5F0',
  card: '#FFFFFF',
  line: '#E5E2D8',
  ink:  '#232821',
  mut:  '#6E766C',
  pro:  '#2E9E7A',
  con:  '#D9614F',
};

/* ────────────────────────────────────────
   Stat chip (Sector Avg / High / Low)
──────────────────────────────────────── */
const StatChip = ({ color, title, value }) => (
  <Tooltip
    title={title}
    arrow
    componentsProps={{
      tooltip: {
        sx: {
          color: '#0d0d0d', fontSize: '12px', fontWeight: 500,
          bgcolor: '#ebf0ff',
          '& .MuiTooltip-arrow': { color: '#ebf0ff' },
        },
      },
    }}
  >
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      fontSize: 11,
      fontWeight: 600,
      color: color,
      background: color + '14',
      border: `1px solid ${color}30`,
      borderRadius: 6,
      padding: '2px 7px',
      cursor: 'default',
      marginRight: 4,
    }}>
      {numberWithCommas(value)}
    </span>
  </Tooltip>
);

/* ────────────────────────────────────────
   Single accordion row
──────────────────────────────────────── */
const AccordionRow = ({ label, detail, isLast }) => {
  const [open, setOpen] = React.useState(false);
  return (
    <div style={{ borderBottom: isLast ? 'none' : `1px solid ${T.line}` }}>
      <button
        onClick={() => setOpen(!open)}
        style={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'none',
          border: 'none',
          padding: '13px 20px',
          cursor: 'pointer',
          textAlign: 'left',
          gap: 12,
        }}
      >
        <span style={{ fontSize: 13.5, fontWeight: 500, color: T.ink, lineHeight: 1.3 }}>
          {label}
        </span>
        <span style={{ fontSize: 18, color: T.mut, fontWeight: 300, flexShrink: 0, lineHeight: 1 }}>
          {open ? '−' : '+'}
        </span>
      </button>

      {open && detail && detail.length > 0 && (
        <div style={{
          padding: '0 16px 16px 16px',
          background: T.bg,
          borderTop: `1px solid ${T.line}`,
        }}>
          {detail.map((d, i) => (
            <div key={d.statement ? d.statement.slice(0, 40) : i} style={{
              marginTop: 12,
              background: T.card,
              borderRadius: 10,
              border: `1px solid ${T.line}`,
              overflow: 'hidden',
            }}>
              {/* Statement / explanation — always on top */}
              {d.statement && (
                <div style={{
                  padding: '10px 14px',
                  fontSize: 13,
                  fontWeight: 500,
                  color: T.ink,
                  lineHeight: 1.55,
                  borderBottom: (d.avg || d.highest || d.lowest) ? `1px solid ${T.line}` : 'none',
                }}>
                  {d.statement}
                </div>
              )}

              {/* Stat chips — below the explanation */}
              {(d.avg || d.highest || d.lowest) && (
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: 8,
                  padding: '10px 14px',
                  background: T.bg,
                }}>
                  {d.avg && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 600, color: T.mut, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Sector Avg</span>
                      <StatChip color="#F59E0B" title={d.avg.meta} value={d.avg.value} />
                    </div>
                  )}
                  {d.highest && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 600, color: T.mut, textTransform: 'uppercase', letterSpacing: '0.06em' }}>High</span>
                      <StatChip color="#4fce54" title={d.highest.meta} value={d.highest.value} />
                    </div>
                  )}
                  {d.lowest && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                      <span style={{ fontSize: 9.5, fontWeight: 600, color: T.mut, textTransform: 'uppercase', letterSpacing: '0.06em' }}>Low</span>
                      <StatChip color="#D9614F" title={d.lowest.meta} value={d.lowest.value} />
                    </div>
                  )}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/* ────────────────────────────────────────
   Pros or Cons card (no dropdown inside)
──────────────────────────────────────── */
const HalfCard = ({ type, data }) => {
  const isPros = type === 'pros';
  const color  = isPros ? T.pro : T.con;
  const label  = isPros ? 'PROS' : 'CONS';

  const slice  = data ? data[type] : null;
  const keys   = slice ? Object.keys(slice)   : [];
  const values = slice ? Object.values(slice) : [];
  const rows   = [];
  keys.forEach((k, ki) => {
    if (values[ki] && values[ki].length > 0) {
      rows.push({ label: k, items: values[ki] });
    }
  });

  return (
    <div style={{
      background: T.card,
      borderRadius: 14,
      border: `1px solid ${T.line}`,
      overflow: 'hidden',
      flex: 1,
    }}>
      {/* Card badge header */}
      <div style={{
        padding: '12px 20px',
        borderBottom: `1px solid ${T.line}`,
        display: 'flex',
        alignItems: 'center',
        gap: 6,
      }}>
        <span style={{
          width: 8, height: 8, borderRadius: '50%',
          background: color, display: 'inline-block', flexShrink: 0,
        }} />
        <span style={{
          fontSize: 10.5, fontWeight: 700, letterSpacing: '0.09em',
          textTransform: 'uppercase', color: color,
        }}>
          {label}
        </span>
      </div>

      {/* Rows */}
      {!data ? (
        <div style={{ padding: 20 }}><Loader /></div>
      ) : (
        rows.map((r, i) => (
          <AccordionRow
            key={r.label}
            label={r.label}
            detail={r.items}
            isLast={i === rows.length - 1}
          />
        ))
      )}
    </div>
  );
};

/* ────────────────────────────────────────
   Main container — one shared year state
──────────────────────────────────────── */
const ProsAndConsContainer = ({ url, yearUrl }) => {
  const [data,        setData]        = React.useState(null);
  const [years,       setYears]       = React.useState([]);
  const [currentYear, setCurrentYear] = React.useState();
  const [dropOpen,    setDropOpen]    = React.useState(false);
  const [yearsError,  setYearsError]  = React.useState(null);
  const [dataError,   setDataError]   = React.useState(null);
  const [yearsTick,   setYearsTick]   = React.useState(0);
  const [dataTick,    setDataTick]    = React.useState(0);

  /* Fetch available years once */
  React.useEffect(() => {
    const fetchYears = async () => {
      setYearsError(null);
      const { data: res, error } = await apiFetch({ url: yearUrl });
      if (error) {
        setYearsError(error);
        return;
      }
      const yrs = res?.years ?? [];
      setYears(yrs);
      setCurrentYear(yrs[yrs.length - 1]);
    };
    fetchYears();
  }, [yearUrl, yearsTick]);

  /* Fetch pros+cons together whenever year changes */
  React.useEffect(() => {
    if (!currentYear) return;
    const fetchData = async () => {
      setDataError(null);
      setData(null);
      const { data: res, error } = await apiFetch({ url: `${url}&date=${currentYear}-03-31` });
      if (error) {
        setDataError(error);
        return;
      }
      setData(res ?? null);
    };
    fetchData();
  }, [url, currentYear, dataTick]);

  /* Legend items */
  const legend = [
    { label: 'Sector Average', color: '#F59E0B' },
    { label: 'High',           color: '#4fce54' },
    { label: 'Low',            color: '#D9614F' },
  ];

  return (
    <div style={{ marginBottom: 16 }}>

      {/* Years fetch failed → whole section error (data depends on year) */}
      {yearsError ? (
        <>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 12 }}>
            <span style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: 16, fontWeight: 700, color: T.ink,
            }}>Pros &amp; Cons</span>
          </div>
          <div style={{ background: T.card, borderRadius: 14, border: `1px solid ${T.line}`, overflow: 'hidden' }}>
            <SectionError message={yearsError.message} onRetry={() => setYearsTick(t => t + 1)} />
          </div>
        </>
      ) : (
        <>
          {/* ── Section header row ── */}
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            flexWrap: 'wrap', gap: 8, marginBottom: 12,
          }}>
            <span style={{
              fontFamily: "'Bricolage Grotesque', sans-serif",
              fontSize: 16, fontWeight: 700, color: T.ink,
            }}>
              Pros &amp; Cons
            </span>

            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
              {years.length > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                  <span style={{ fontSize: 11, color: T.mut, fontWeight: 500 }}>Back Testing</span>
                  <Dropdown isOpen={dropOpen} toggle={() => setDropOpen(!dropOpen)}>
                    <DropdownToggle caret style={{
                      fontSize: 11, padding: '3px 10px', borderRadius: 999,
                      border: `1px solid ${T.line}`, background: T.bg,
                      color: T.ink, fontWeight: 600, boxShadow: 'none',
                    }}>
                      {currentYear}
                    </DropdownToggle>
                    <DropdownMenu container="body" style={{
                      maxHeight: 200, overflowY: 'auto', borderRadius: 10,
                      border: `1px solid ${T.line}`, boxShadow: '0 4px 16px rgba(0,0,0,.08)',
                      padding: '4px 0',
                    }}>
                      {years.map((y) => (
                        <DropdownItem key={y} onClick={() => setCurrentYear(y)} style={{
                          fontSize: 12,
                          fontWeight: y === currentYear ? 700 : 400,
                          color: y === currentYear ? T.pro : T.ink,
                          padding: '6px 16px',
                        }}>
                          {y}
                        </DropdownItem>
                      ))}
                    </DropdownMenu>
                  </Dropdown>
                </div>
              )}

              <div style={{ width: 1, height: 16, background: T.line }} />

              {legend.map(({ label, color }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
                  <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block' }} />
                  <span style={{ fontSize: 11, color: T.mut, fontWeight: 500 }}>{label}</span>
                </div>
              ))}
            </div>
          </div>

          {/* ── Two cards side by side (or data error) ── */}
          <div style={{ display: 'flex', gap: 12, alignItems: 'stretch' }}>
            {dataError ? (
              <div style={{
                flex: 1, background: T.card, borderRadius: 14,
                border: `1px solid ${T.line}`, overflow: 'hidden',
              }}>
                <SectionError
                  message={dataError.message}
                  onRetry={() => { setData(null); setDataTick(t => t + 1); }}
                />
              </div>
            ) : (
              <>
                <HalfCard type="pros" data={data} />
                <HalfCard type="cons" data={data} />
              </>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default ProsAndConsContainer;

